import type { Truck } from '@numeo/types-core'
import type { TruckDraft } from '../utils/truckFromVerification'
import spotApi from 'services/spotApi'
import { boxTruckEquipmentSnapshot, buildOptimisticPatch, emptyBoxTruckDetails } from 'pages/dashboard/pages/profiles/shared/boxTruckEquipmentSave'
import { adoptDriverVehicleDocumentsToAsset, updateBoxTruckAssetDrivers, updateBoxTruckAssetProfile } from './boxTruckProfilesApi'

interface CreateAssetForDriverArgs {
    truck: TruckDraft
    /** The FLEET driver record the claim created — not the account id the queue row carries. */
    driverId?: string
    /** Re-submit after the operator accepted a seat overage. */
    acceptOverage?: boolean
}

interface CreatedBoxTruckAsset {
    truckId: string
    /** The Box Truck block — vehicle type, payload, registration, cargo box — did not land. */
    detailsFailed: boolean
    /** The driver could not be seated on the vehicle. */
    assignmentFailed: boolean
    /** The driver's registration, insurance and cargo photo were not put on the vehicle. */
    documentsFailed: boolean
}

/**
 * Create a Box Truck vehicle and seat one driver on it.
 *
 * Deliberately goes through `POST /trucks` rather than writing the vehicle from a Box Truck endpoint: that
 * route is where the seat/billing guard lives, and a second door into truck creation would be a second way
 * to exceed a tenant's paid trucks. The refusals it answers with (402 trial limit, 409 overage confirm) are
 * passed back up untouched so the caller can show the same prompt Assets does.
 *
 * `boxTruck` is sent separately because the generic truck endpoint REFUSES that block by design ("boxTruck
 * source metadata cannot be changed through the generic truck endpoint"), and `dispatchers: []` because
 * nothing in Box Truck reads them while Groups (ADR-027) rejects a desk that is not in the driver's group.
 */
export const createBoxTruckAssetForDriver = async ({ truck, driverId, acceptOverage }: CreateAssetForDriverArgs): Promise<CreatedBoxTruckAsset> => {
    // `boxTruck` is written separately (the generic endpoint refuses it), `drivers` through the roster call,
    // and an `_id` on a CREATE is a draft's placeholder that must not be sent as an identity.
    const root: Partial<TruckDraft> = { ...truck }
    delete root.boxTruck
    delete root.drivers
    delete (root as { _id?: unknown })._id
    // `spotApi.post` already unwraps the axios envelope, so the created vehicle is `body.data` — the same
    // one level Assets reads. Digging a second level finds nothing and reads as "the server said nothing".
    // The same normalisation the Assets create path applies. The draft carries the empty-truck defaults, so
    // a blank deadhead reaches the server as `''` — Assets turns that into the 150 a truck is searched with,
    // and two paths that create the same record must not disagree about what "not set" means.
    const response: unknown = await spotApi.post('/trucks', {
        ...root,
        deadHeadOrigin: root.deadHeadOrigin === '' ? '150' : root.deadHeadOrigin,
        deadHeadDestination: root.deadHeadDestination === '' ? '150' : root.deadHeadDestination,
        dispatchers: [],
        acceptOverage,
    })
    const created = (response as { data?: Truck })?.data
    const truckId = created?._id ? String(created._id) : undefined
    if (!truckId) throw new Error('The truck was created but the server did not say which one — check Assets before adding another.')

    // PAST THIS LINE NOTHING THROWS. The vehicle is committed: rejecting here would have the caller report
    // that it could not be created and invite the operator to add it again, which is how a tenant ends up
    // with two of the same truck. Each remaining step reports itself instead, and each is repeatable — the
    // details are a compare-and-set, the roster is idempotent, and the adoption upserts on the file's path.

    // Compare-and-set against a vehicle that has NO profile yet, which is what the endpoint expects: the
    // same shape Assets sends, built by the same two helpers, so the two paths cannot drift on what a Box
    // Truck detail write looks like.
    const details = boxTruckEquipmentSnapshot(truck)?.boxTruck ?? emptyBoxTruckDetails()
    const patch = buildOptimisticPatch(details, emptyBoxTruckDetails())
    let detailsFailed = false
    if (Object.keys(patch.changes).length > 0) {
        try {
            await updateBoxTruckAssetProfile(truckId, patch)
        } catch (error) {
            detailsFailed = true
            console.error('[box-truck] vehicle details were not saved onto the new truck', error)
        }
    }

    // One call seats the driver: the roster endpoint writes `truck.drivers[]` AND the driver's own `truckId`.
    let assignmentFailed = false
    let documentsFailed = false
    if (driverId) {
        try {
            await updateBoxTruckAssetDrivers(truckId, [driverId])
        } catch (error) {
            assignmentFailed = true
            console.error('[box-truck] the driver was not seated on the new truck', error)
        }
        // The vehicle's own paperwork, which the driver submitted before any vehicle existed to hold it.
        try {
            // A PARTIAL adoption answers 200 with a `failed` count — some paperwork landed and some did not,
            // which is not success. Reading only the rejection would have shown the green toast over a
            // vehicle missing half its documents.
            const { failed } = await adoptDriverVehicleDocumentsToAsset(truckId, driverId)
            if (failed > 0) {
                documentsFailed = true
                console.error(`[box-truck] ${failed} vehicle document(s) were not adopted onto the new truck`)
            }
        } catch (error) {
            documentsFailed = true
            console.error('[box-truck] vehicle documents were not adopted onto the new truck', error)
        }
    }
    return { truckId, detailsFailed, assignmentFailed, documentsFailed }
}
